<template>
  <div>
    <Message type="info"> この情報でよろしければ、保存ボタンをクリックしてください。 </Message>
    <div class="instagram-confirm-section">
      <Message type="warning">
        Instagramでは「いいね！」やフォロー、コメントを含むやり取りの見返りに、金銭や金券などの提供を申し出ることは、禁じられています。
        <br />
        <a href="https://www.facebook.com/help/instagram/477434105621119" target="_blank">
          参考: Instagramコミュ二ティガイドライン
        </a>
      </Message>
    </div>

    <div class="instagram-confirm-section">
      <Panel>
        <div class="section-title">基本設定</div>

        <div class="section-item">
          実施アカウント
          <div class="section-item-content">
            <Account v-if="account" :name="account.name" :image="account.picture_url" :symbol="account.target_sns" />
          </div>
        </div>

        <div class="section-item">
          キャンペーンタイトル
          <div class="section-item-content">
            {{ form.title }}
          </div>
        </div>

        <div class="section-item">
          キャンペーン期間
          <div class="section-item-content">
            {{ campaign_period }}
          </div>
        </div>
      </Panel>
    </div>

    <div class="instagram-confirm-section">
      <Panel>
        <div class="section-title">応募条件</div>

        <div class="section-item">
          キャンペーンタイプ
          <div class="section-item-content">
            {{ campaign_type }}
          </div>
        </div>
        <div class="section-item">
          応募条件
          <div class="section-item-content">
            {{ entry_action_type }}
          </div>
        </div>

        <div class="section-item">
          以下のキャンペーンハッシュタグ全てを含む
          <div class="section-item-content tag-container">
            <Tags :value="form.keywords" />
          </div>
        </div>
      </Panel>
      <div v-if="form.entry_action_type === 'comment' && this.form.campaign_type === 'instantwin'" class="section-item">
        <Message type="warning">
          最初にハッシュタグ付きで投稿したもののみが、キャンペーン投稿として判定されます。
          <br />
          この投稿を削除した場合、再度同じハッシュタグで投稿しても応募は収集されません。
          <br />
          詳しくは<a
            href="https://help-atelu.comnico.jp/ig-instantwin-post-notes"
            target="_blank"
            rel="noopener noreferrer"
            >こちら</a
          >をご確認ください。
          <div class="confirm-checkbox">
            <el-checkbox label="確認しました" v-model="is_check" />
          </div>
        </Message>
      </div>
    </div>

    <div class="confirm-footer">
      <el-button @click="prevStep">
        <Icon name="arrow-left" space="right" />
        戻る
      </el-button>
      <div class="footer-right">
        <el-button @click="cancel"> キャンセル </el-button>
        <el-button type="primary" :disabled="is_submit || comment_instantwin" @click="save"> 保存 </el-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment-timezone'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import Message from '@/client/components/_atoms/Message.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import Account from '@/client/components/_molecules/Account.vue'
import Tags from '@/client/components/_molecules/Tags.vue'
import { INSTAGRAM_CAMPAIGN_TYPES, INSTAGRAM_ENTRY_ACTION_TYPES } from '@/client/constant'
import { TrackingService } from '@/client/services'
import { useCampaignDialogStore } from '@/client/store/useCampaignDialogStore'

export default defineComponent({
  name: 'CampaignCreateInstagramConfirm',
  components: {
    Icon,
    Message,
    Panel,
    Account,
    Tags
  },
  data() {
    return {
      is_submit: false,
      is_check: false
    }
  },
  computed: {
    ...mapState(useCampaignDialogStore, ['form', 'api_accounts']),
    comment_instantwin() {
      return this.form.entry_action_type === 'comment' && this.form.campaign_type === 'instantwin' && !this.is_check
    },
    account() {
      return this.api_accounts.find(v => v.id === this.form.account_id && v.target_sns === 'instagram')
    },
    campaign_period() {
      const start_datetime = moment(`${this.form.start_date} ${this.form.start_time}`, 'YYYY-MM-DD HH:mm')
      const end_datetime = moment(`${this.form.end_date} ${this.form.end_time}`, 'YYYY-MM-DD HH:mm')

      const format = 'YYYY年MM月DD日 HH:mm'

      return `${start_datetime.format(format)} - ${end_datetime.format(format)}`
    },
    campaign_type() {
      const type = INSTAGRAM_CAMPAIGN_TYPES.find(v => v.value === this.form.campaign_type)

      return type ? type.label : ''
    },
    entry_action_type() {
      const type = INSTAGRAM_ENTRY_ACTION_TYPES.find(v => v.value === this.form.entry_action_type)

      return type ? type.label : ''
    }
  },
  methods: {
    ...mapActions(useCampaignDialogStore, ['prevStep', 'createCampaign']),
    cancel(payload: unknown) {
      this.$emit('cancel', payload)
      return payload
    },
    /**
     * キャンペーンを保存する
     */
    async save() {
      TrackingService.sendEvent('click:キャンペーンの新規作成|確認|保存')

      if (this.is_submit) {
        return
      }

      this.is_submit = true

      const result = await this.createCampaign()

      this.is_submit = false

      if (result.data && result.data.id) {
        this.$emit('complete', result.data.id)
        return
      }

      if (result.error && result.error.type === 'MAX_CAMPAIGN_COUNT_OVER') {
        this.$notify({
          title: 'キャンペーンの作成に失敗しました。',
          message: '同時開催可能件数を超えるため、指定の期間ではキャンペーンを開催できません。',
          customClass: 'danger'
        })
        return
      }

      if (result.error && result.error.type === 'CAMPAIGN_START_DATETIME_INVALID') {
        this.$notify({
          title: 'キャンペーンの作成に失敗しました。',
          message: 'キャンペーン開始日時は、現在日時より後を指定してください。',
          customClass: 'danger'
        })
        return
      }

      if (result.error && result.error.type === 'FACEBOOK_PAGE_NOT_FOUND') {
        this.$notify({
          title: 'キャンペーンの作成に失敗しました。',
          message:
            'Instagramインスタンタウィンを実施するうえで必要なFacebookページが見つかりません。アクセストークンを更新してください。',
          customClass: 'danger'
        })
        return
      }

      if (result.error && result.error.type === 'INSTAGRAM_MEDIA_UPLOAD_FAILED') {
        this.$notify({
          title: 'キャンペーンの作成に失敗しました。',
          message:
            'Instagramインスタンタウィンを実施するうえで必要な処理に失敗しました。アクセストークンを更新してください。',
          customClass: 'danger'
        })
        return
      }

      this.$notify({
        title: 'キャンペーンの作成に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger'
      })
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-confirm-section {
  margin-top: $spacing-unit * 2;

  .section-title {
    font-weight: bold;
  }

  .section-item {
    margin-top: $spacing-unit * 3;

    .section-item-content {
      margin-top: $spacing-unit;

      &:not(.tag-container) {
        font-size: $font-large-size;
      }
    }
  }
}

.confirm-checkbox {
  margin-top: $round;
}

.confirm-footer {
  display: flex;
  justify-content: space-between;
  margin-top: $spacing-unit * 3;
}

.footer-right {
  display: flex;
}
</style>
